// Minimal fetch-based GET client for the public site's shared (layout / home)
// code path. It replaces axios there so axios (~15 KB gzipped) is no longer
// part of every public page's initial JavaScript; the admin panel and the
// feature pages that need uploads, interceptors or non-GET verbs still use
// axios directly.
//
// It keeps the axios behaviour the callers rely on:
//   - resolves to { data, status, statusText, headers }
//   - `data` is parsed JSON when the body is JSON, otherwise the raw text
//   - non-2xx rejects with an Error carrying `error.response.{status,data}`
//     and the same "Request failed with status code N" message
//   - a network failure rejects with message "Network Error" and no `response`
//   - `params` are appended to the query string (null/undefined skipped,
//     arrays serialised as key[]=a&key[]=b like axios)
//   - credentials are not sent cross-origin (axios' withCredentials default)

export class HttpError extends Error {
  constructor(message, { response, cause } = {}) {
    super(message);
    this.name = "HttpError";
    this.isAxiosError = true; // callers/guards written against axios keep working
    this.response = response;
    if (cause) this.cause = cause;
  }
}

function withParams(url, params) {
  if (!params) return url;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      value.forEach((item) => search.append(`${key}[]`, item));
    } else {
      search.append(key, value instanceof Date ? value.toISOString() : value);
    }
  }
  const query = search.toString();
  if (!query) return url;
  return url + (url.includes("?") ? "&" : "?") + query;
}

function parseBody(text) {
  if (!text) return text;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

async function get(url, { params, headers, signal, withCredentials } = {}) {
  let res;
  try {
    res = await fetch(withParams(url, params), {
      method: "GET",
      headers,
      signal,
      credentials: withCredentials ? "include" : "same-origin",
    });
  } catch (cause) {
    if (cause?.name === "AbortError") throw cause;
    throw new HttpError("Network Error", { cause });
  }

  const response = {
    data: parseBody(await res.text()),
    status: res.status,
    statusText: res.statusText,
    headers: res.headers,
  };
  if (!res.ok) {
    throw new HttpError(`Request failed with status code ${res.status}`, { response });
  }
  return response;
}

const http = { get };
export default http;
